// @ts-nocheck
import {Column} from 'react-table'
import {UserCustomHeader} from '../apps/user-management/users-list/table/columns/UserCustomHeader'
import {Expense, deleteExpense} from './api'
import {KTIcon} from '../../../_metronic/helpers'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-hot-toast'
import {useState} from 'react'
import {useLayout} from '../../../_metronic/layout/core'

// ─── Helper ───────────────────────────────────────────────────────────────────

const formatSlug = (slug: string): string => {
  if (!slug) return 'N/A'
  return slug
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// ─── Columns ──────────────────────────────────────────────────────────────────

const getExpenseColumns = (
  costCenterMap: Record<number, string>
): ReadonlyArray<Column<Expense>> => [
  {
    Header: (props) => (
      <UserCustomHeader
        tableProps={props}
        title='Expense ID'
        className='min-w-100px ps-4 text-center'
      />
    ),
    id: 'expense_id',
    accessor: 'expense_id',
    sortType: 'basic',
    Cell: ({...props}) => (
      <span className='badge badge-light-dark fw-bold'>
        EXP-{props.data[props.row.index].expense_id}
      </span>
    ),
  },
  {
    Header: (props) => (
      <UserCustomHeader tableProps={props} title='Title' className='min-w-175px text-center' />
    ),
    accessor: 'expense_title',
    sortType: 'alphanumeric',
    Cell: ({...props}) => {
      const ex: Expense = props.data[props.row.index]
      return (
        <div className='d-flex flex-column'>
          <span className='fw-bold'>{ex.expense_title}</span>
          <span className='text-muted fs-7'>{ex.description?.substring(0, 30)}...</span>
        </div>
      )
    },
  },
  {
    Header: (props) => (
      <UserCustomHeader tableProps={props} title='Category' className='min-w-125px text-center' />
    ),
    accessor: 'category',
    sortType: 'string',
    Cell: ({...props}) => <div>{formatSlug(props.data[props.row.index].category)}</div>,
  },
  {
    Header: (props) => (
      <UserCustomHeader tableProps={props} title='Amount' className='min-w-125px text-center' />
    ),
    accessor: 'amount',
    sortType: 'basic',
    Cell: ({...props}) => {
      const ex: Expense = props.data[props.row.index]
      return (
        <div>
          {ex.currency} &#8377;{' '}
          {Number(ex.amount || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}
        </div>
      )
    },
  },
  {
    Header: (props) => (
      <UserCustomHeader
        tableProps={props}
        title='Cost Centre'
        className='min-w-125px text-center'
      />
    ),
    accessor: 'cost_center_id',
    sortType: 'basic',
    Cell: ({...props}) => {
      const costCenterId = props.data[props.row.index].cost_center_id
      return <div>{costCenterMap[costCenterId] ?? 'N/A'}</div>
    },
  },
  {
    Header: (props) => (
      <UserCustomHeader tableProps={props} title='Status' className='min-w-100px text-center' />
    ),
    accessor: 'status',
    sortType: 'string',
    Cell: ({...props}) => {
      const status = props.data[props.row.index].status?.toLowerCase()
      const badgeClass =
        status === 'approved'
          ? 'badge-light-success'
          : status === 'pending'
          ? 'badge-light-warning'
          : 'badge-light-danger'
      return (
        <span className={`badge fw-bold ${badgeClass}`}>
          {(props.data[props.row.index].status || 'PENDING').toUpperCase()}
        </span>
      )
    },
  },
  {
    Header: (props) => (
      <UserCustomHeader tableProps={props} title='Date' className='min-w-100px text-center' />
    ),
    accessor: 'expense_date',
    sortType: 'alphanumeric',
  },
  {
    Header: (props) => (
      <UserCustomHeader tableProps={props} title='Actions' className='min-w-100px text-center' />
    ),
    accessor: 'actions',
    sortable: false,
    Cell: ({...props}) => {
      const {company} = useLayout()
      const [showDeleteModal, setShowDeleteModal] = useState(false)

      const handleDelete = async () => {
        deleteExpense(company.value, props.data[props.row.index].expense_id)
          .then((res) => res?.detail && toast.success(res.detail))
          .catch((err) => err && toast.error(err))
          .finally(() => setShowDeleteModal(false))
      }

      return (
        <>
          <div className='d-flex justify-content-center flex-shrink-0'>
            <div
              data-bs-toggle='tooltip'
              data-bs-trigger='hover'
              data-bs-dismiss-='click'
              title='Delete'
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
              onClick={() => setShowDeleteModal(true)}
            >
              <KTIcon iconName='trash' className='fs-3' />
            </div>
          </div>

          <Modal
            className='modal-sticky modal-sticky-lg modal-sticky-bottom-right'
            id='delete_expense'
            role='dialog'
            data-backdrop='false'
            aria-hidden='true'
            tabIndex='-1'
            show={showDeleteModal}
            animation={false}
          >
            <div className='modal-content'>
              {/*begin::Header*/}
              <div className='d-flex align-items-center justify-content-between py-5 ps-8 pe-5 border-bottom'>
                <h5 className='fw-bold m-0'>Delete Expense</h5>
                <div className='d-flex ms-2'>
                  {/*begin::Close*/}
                  <div
                    className='btn btn-icon btn-sm btn-light-primary ms-2'
                    data-bs-dismiss='modal'
                    onClick={() => setShowDeleteModal(false)}
                  >
                    <KTIcon className='fs-1' iconName='cross' />
                  </div>
                  {/*end::Close*/}
                </div>
              </div>
              {/*end::Header*/}
              <div className='text-center py-5 px-5'>
                Are you sure you want to delete this Expense record? This action cannot be undone.
              </div>
              <div className='text-center pt-5 pb-5'>
                <button
                  type='reset'
                  onClick={() => setShowDeleteModal(false)}
                  className='btn btn-light me-3'
                  data-kt-users-modal-action='cancel'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  onClick={handleDelete}
                >
                  Proceed
                </button>
              </div>
            </div>
          </Modal>
        </>
      )
    },
  },
]

export {getExpenseColumns}
